/**
 * Styled in-app dialogs — replaces native alert()/confirm() so the admin
 * panel never shows browser chrome popups.
 *
 *  - toast(message, type)  -> slide-in notification, auto-dismisses
 *  - confirmDialog(msg)    -> Promise<boolean>, styled modal with Cancel/OK
 *  - installDialogOverrides() -> global window.alert -> toast (call once at boot)
 */

type ToastType = 'success' | 'error' | 'info' | 'warning';

let hostEl: HTMLElement | null = null;

function ensureHost(): HTMLElement {
  if (hostEl && document.body.contains(hostEl)) return hostEl;
  hostEl = document.createElement('div');
  hostEl.id = 'babuba-dialog-host';
  hostEl.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:9999',
    'pointer-events:none', 'font-family:Inter,system-ui,sans-serif',
  ].join(';');
  document.body.appendChild(hostEl);
  return hostEl;
}

/* ── Toast ─────────────────────────────────────────────── */

const TOAST_COLORS: Record<ToastType, { bg: string; border: string; icon: string }> = {
  success: { bg: '#ecfdf5', border: '#a7f3d0', icon: '✓' },
  error:   { bg: '#fef2f2', border: '#fecaca', icon: '✕' },
  warning: { bg: '#fffbeb', border: '#fde68a', icon: '!' },
  info:    { bg: '#eef2ff', border: '#c7d2fe', icon: 'i' },
};

export function toast(message: string, type: ToastType = 'info', duration = 4000) {
  const host = ensureHost();
  const el = document.createElement('div');
  const c = TOAST_COLORS[type];
  el.style.cssText = [
    'position:fixed', 'top:20px', 'right:20px', 'max-width:420px',
    `background:${c.bg}`, `border:1px solid ${c.border}`, 'border-radius:12px',
    'padding:12px 16px', 'box-shadow:0 8px 24px rgba(15,23,42,.12)',
    'display:flex', 'align-items:flex-start', 'gap:10px',
    'font-size:13.5px', 'line-height:1.45', 'color:#1e293b',
    'transform:translateX(120%)', 'transition:transform .25s ease-out',
    'pointer-events:auto', 'cursor:pointer',
  ].join(';');
  el.innerHTML = `
    <span style="flex:none;width:18px;height:18px;border-radius:50%;
      background:${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : type === 'warning' ? '#f59e0b' : '#6366f1'};
      color:#fff;display:flex;align-items:center;justify-content:center;
      font-size:11px;font-weight:700;margin-top:1px;">${c.icon}</span>
    <span>${String(message).replace(/</g, '&lt;')}</span>`;
  el.addEventListener('click', () => el.remove());
  host.appendChild(el);
  requestAnimationFrame(() => { el.style.transform = 'translateX(0)'; });
  setTimeout(() => {
    el.style.transform = 'translateX(120%)';
    setTimeout(() => el.remove(), 300);
  }, duration);
}

/* ── Confirm modal ─────────────────────────────────────── */

let modalResolve: ((v: boolean) => void) | null = null;

export function confirmDialog(message: string, okLabel = 'Confirm', danger = false): Promise<boolean> {
  // Only one modal at a time — resolve previous as cancelled if stacked
  if (modalResolve) { modalResolve(false); modalResolve = null; }

  const host = ensureHost();
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:9998',
    'background:rgba(15,23,42,.45)', 'backdrop-filter:blur(2px)',
    'display:flex', 'align-items:center', 'justify-content:center',
    'pointer-events:auto', 'opacity:0', 'transition:opacity .18s ease-out',
  ].join(';');

  const box = document.createElement('div');
  box.style.cssText = [
    'width:min(92vw,420px)', 'background:var(--dialog-bg)', 'border-radius:16px',
    'padding:24px', 'box-shadow:0 24px 64px rgba(15,23,42,.28)',
    'transform:scale(.96)', 'transition:transform .18s ease-out',
    'font-family:Inter,system-ui,sans-serif', 'color:var(--dialog-fg)',
  ].join(';');
  box.innerHTML = `
    <div style="font-size:15px;font-weight:700;color:var(--dialog-title);margin-bottom:8px;">Please confirm</div>
    <div style="font-size:13.5px;line-height:1.55;color:var(--dialog-muted);margin-bottom:20px;">${String(message).replace(/</g, '&lt;')}</div>
    <div style="display:flex;justify-content:flex-end;gap:10px;">
      <button data-act="cancel" style="padding:8px 16px;border-radius:10px;border:1px solid var(--dialog-border);background:var(--dialog-bg);color:var(--dialog-muted);font-size:13px;font-weight:600;cursor:pointer;">Cancel</button>
      <button data-act="ok" style="padding:8px 16px;border-radius:10px;border:none;background:${danger ? '#dc2626' : '#4f46e5'};color:#fff;font-size:13px;font-weight:600;cursor:pointer;">${okLabel.replace(/</g, '&lt;')}</button>
    </div>`;

  overlay.appendChild(box);
  host.appendChild(overlay);
  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
    box.style.transform = 'scale(1)';
  });

  const done = (v: boolean) => {
    overlay.style.opacity = '0';
    box.style.transform = 'scale(.96)';
    setTimeout(() => overlay.remove(), 180);
    modalResolve = null;
    if (res) res(v);
  };
  let res: ((v: boolean) => void) | null = null;
  const promise = new Promise<boolean>((r) => { res = r; });
  modalResolve = (v: boolean) => done(v);

  overlay.addEventListener('click', (e) => { if (e.target === overlay) done(false); });
  box.querySelector('[data-act="cancel"]')!.addEventListener('click', () => done(false));
  box.querySelector('[data-act="ok"]')!.addEventListener('click', () => done(true));
  // Escape to cancel
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { done(false); document.removeEventListener('keydown', onKey); } };
  document.addEventListener('keydown', onKey);

  return promise;
}

/* ── Global override ───────────────────────────────────── */

export function installDialogOverrides() {
  if (typeof window === 'undefined') return;
  if ((window as any).__babubaDialogsInstalled) return;
  (window as any).__babubaDialogsInstalled = true;
  // alert -> toast (native alert is jarring and blocks the thread)
  window.alert = (msg?: unknown) => toast(String(msg ?? ''), 'error');
  // confirm -> styled modal (async; call sites use await confirmDialog()).
  // Keep window.confirm as a safe fallback returning false so stray calls
  // never auto-proceed with destructive actions.
  window.confirm = () => false;
}

export default { toast, confirmDialog, installDialogOverrides };
